import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/common/backButton";
import useAuthStore from "../../store/authStore";
import { calculatePregnancyInfo } from "../../utils/pregnancyUtils";
import twinsBabyData from "../../data/twinsBabyData";
import singleBabyData from "../../data/singleBabyData";
import postBirthData from "../../data/postBirthData";
import title_logo from "../../images/title_logo.png"

const CardDetail = () => {
  const navigate = useNavigate();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userInfo = useAuthStore((state) => state.userInfo);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      alert("로그인이 필요합니다.");
    }
  }, [isAuthenticated, navigate]);

  // 임신주차 정보 계산
  const pregnancyInfo = useMemo(
    () => calculatePregnancyInfo(userInfo?.baby?.[0]?.baby_birth),
    [userInfo?.baby]
  );

  const getCurrentWeekData = () => {
    if (pregnancyInfo.isPostpartum) {
      const daysSinceBirth = pregnancyInfo.daysSinceBirth || 1;

      const exactDayData = postBirthData.datas.find(
        (data) => data.day === daysSinceBirth
      );

      if (exactDayData) {
        return exactDayData;
      }

      const availableDays = postBirthData.datas
        .filter((data) => data.day <= daysSinceBirth)
        .sort((a, b) => b.day - a.day);

      if (availableDays.length > 0) {
        return availableDays[0];
      }

      return postBirthData.datas[0];
    }

    let week = pregnancyInfo.week;
    const babyType = userInfo?.baby?.[0]?.baby_type;
    const data =
      babyType === "TWIN" || babyType === "twins"
        ? twinsBabyData
        : singleBabyData;

    // 쌍둥이의 경우 1, 2주차 데이터가 없으므로 0주차 데이터 사용
    if ((babyType === "TWIN" || babyType === "twins") && (week === 1 || week === 2)) {
      week = 0;
    }

    return (
      data.datas.find((data) => data.week === week) ||
      data.datas[data.datas.length - 1]
    );
  };

  const currentWeekData = getCurrentWeekData();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGoHome = () => {
    navigate("/main");
  };

  return (
    <div
      className="flex flex-col bg-white pt-[3.125rem] md:pt-[4.375rem] pb-[1.875rem]"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <BackButton clickEvent={handleGoHome} imageUrl={title_logo} imageAlt="title_logo" />

      <div className="block w-[calc(100%-2.875rem)] mx-auto pt-[1.875rem] md:pt-[2.125rem] max-w-[37.5rem]">
        <div className="space-y-6">
          {isAuthenticated && userInfo && (
            <div className="text-center p-4 bg-[#FFF7E6] rounded-lg border shadow-md">
              <h1 className="text-xl md:text-2xl font-bold">
                {pregnancyInfo.isPostpartum
                  ? `출산 ${pregnancyInfo.daysSinceBirth}일 정보`
                  : `임신 ${pregnancyInfo.week}주차 정보`}
              </h1>
            </div>
          )}

          {/* 아기 정보 */}
          {currentWeekData?.baby && (
            <div className="bg-[#FFF7E6] rounded-lg p-6 border shadow-md">
              <div className="flex flex-col items-center border-b border-[#E9EAEE] pb-6 mb-6">
                <h2 className="text-xl font-semibold leading-[1.9375rem] mb-2 text-center whitespace-pre-line">
                  🍼 {currentWeekData.baby.head.title}
                </h2>
                <p className="text-base text-[#888888] text-center font-semibold">
                  {currentWeekData.baby.head.discribed}
                </p>
                {currentWeekData.baby.head.size && (
                  <span className="text-sm text-[#B6B7B9] text-center mt-2">
                    {currentWeekData.baby.head.size}
                  </span>
                )}
              </div>
              <div className="space-y-6">
                {currentWeekData.baby.body.map((item, index) => (
                  <div key={index}>
                    <h3 className="text-[1.0625rem] font-semibold mb-2 text-[#FF931D]">
                      {item.subTitle}
                    </h3>
                    <p className="text-sm whitespace-pre-line">
                      {item.discribed}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 엄마 정보 */}
          {currentWeekData?.mom && (
            <div className="bg-[#FEF3F2] rounded-lg p-6 border shadow-md">
              <div className="flex flex-col items-center border-b border-[#E9EAEE] pb-6 mb-6">
                <h2 className="text-xl font-semibold leading-[1.9375rem] mb-2 text-center whitespace-pre-line">
                  🤱 {currentWeekData.mom.head.title}
                </h2>
                <p className="text-base text-[#888888] text-center font-semibold">
                  {currentWeekData.mom.head.discribed}
                </p>
              </div>
              <div className="space-y-6">
                {currentWeekData.mom.body.map((item, index) => (
                  <div key={index}>
                    <h3 className="text-[1.0625rem] font-semibold mb-2 text-[#EF4444]">
                      {item.subTitle}
                    </h3>
                    <p className="text-sm whitespace-pre-line">
                      {item.discribed}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 아빠 정보 */}
          {currentWeekData?.dad && (
            <div className="bg-[#F0F9FF] rounded-lg p-6 border shadow-md mb-[1.875rem]">
              <div className="flex flex-col items-center border-b border-[#E9EAEE] pb-6 mb-6">
                <h2 className="text-xl font-semibold leading-[1.9375rem] mb-2 text-center whitespace-pre-line">
                  👨‍👩‍👧‍👦 {currentWeekData.dad.head.title}
                </h2>
                <p className="text-base text-[#888888] text-center font-semibold">
                  {currentWeekData.dad.head.discribed}
                </p>
              </div>
              <div className="space-y-6">
                {currentWeekData.dad.body.map((item, index) => (
                  <div key={index}>
                    <h3 className="text-[1.0625rem] font-semibold mb-2 text-[#3B82F6]">
                      {item.subTitle}
                    </h3>
                    <p className="text-sm whitespace-pre-line">
                      {item.discribed}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardDetail;
